import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, 
  LogOut,
  Users,
  AlertTriangle,
  Settings,
  Search,
  Loader2,
  LayoutDashboard,
  Briefcase,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import type { Agent, Debtor, Case } from '../types';

interface DebtorsPageProps {
  user: Agent;
  onLogout: () => void;
}

interface DebtorWithStats extends Debtor {
  totalCases: number;
  totalDebt: number;
}

export default function DebtorsPage({ user, onLogout }: DebtorsPageProps) {
  const [debtors, setDebtors] = useState<DebtorWithStats[]>([]);
  const [filteredDebtors, setFilteredDebtors] = useState<DebtorWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [segmentFilter, setSegmentFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    loadDebtors();
  }, []);

  useEffect(() => {
    filterDebtors();
  }, [debtors, searchTerm, segmentFilter, typeFilter]);

  const loadDebtors = async () => {
    setIsLoading(true);
    try {
      const [debtorsResult, casesResult] = await Promise.all([
        serenityClient.queryData<Debtor>('SELECT * FROM Debtors ORDER BY created_at DESC'),
        serenityClient.queryData<Case>('SELECT debtor_id, total_amount, amount_paid FROM Cases'),
      ]);

      // Calculate stats per debtor
      const debtorsWithStats: DebtorWithStats[] = debtorsResult.map(debtor => {
        const debtorCases = casesResult.filter(c => c.debtor_id === debtor.debtor_id);
        const totalDebt = debtorCases.reduce((sum, c) => sum + (c.total_amount - (c.amount_paid || 0)), 0);
        return {
          ...debtor,
          totalCases: debtorCases.length,
          totalDebt,
        };
      });

      setDebtors(debtorsWithStats);
    } catch (error) {
      console.error('Failed to load debtors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterDebtors = () => {
    let filtered = [...debtors];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.first_name.toLowerCase().includes(term) ||
        (d.last_name && d.last_name.toLowerCase().includes(term)) ||
        d.email.toLowerCase().includes(term) ||
        d.debtor_id.toLowerCase().includes(term)
      );
    }

    if (segmentFilter !== 'all') {
      filtered = filtered.filter(d => d.segment === segmentFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(d => d.debtor_type === typeFilter);
    }

    setFilteredDebtors(filtered);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getSegmentColor = (segment: string) => {
    switch (segment) {
      case 'high': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/cases', icon: Briefcase, label: 'Cases' },
    { path: '/debtors', icon: Users, label: 'Debtors', active: true },
    { path: '/requests', icon: AlertTriangle, label: 'Requests' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-semibold">DFS Collections</h1>
              <p className="text-xs text-slate-400">Agent Portal</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    item.active ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-sm font-medium">
                {user.first_name[0]}{user.last_name[0]}
              </div>
              <div>
                <p className="text-sm font-medium">{user.first_name}</p>
                <p className="text-xs text-slate-400">{user.role}</p>
              </div>
            </div>
            <button onClick={onLogout} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Debtors</h1>
            <p className="text-gray-500">Manage debtor profiles and information</p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search debtors..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <select
                value={segmentFilter}
                onChange={(e) => setSegmentFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Segments</option>
                <option value="high">High Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="low">Low Priority</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                <option value="individual">Individual</option>
                <option value="corporate">Corporate</option>
              </select>
            </div>
          </div>

          {/* Debtors Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredDebtors.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-500">
              No debtors found matching your criteria.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDebtors.map((debtor) => (
                <div key={debtor.debtor_id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-lg font-semibold text-blue-600">
                        {debtor.first_name[0]}{debtor.last_name?.[0] || ''}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {debtor.debtor_type === 'corporate' ? debtor.first_name : `${debtor.first_name} ${debtor.last_name || ''}`}
                        </p>
                        <p className="text-sm text-gray-500">{debtor.debtor_type}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${getSegmentColor(debtor.segment)}`}>
                      {debtor.segment}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{debtor.email}</span>
                    </div>
                    {debtor.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        <span>{debtor.phone}</span>
                      </div>
                    )}
                    {debtor.city && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>{debtor.city}, {debtor.country}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div>
                      <p className="text-sm text-gray-500">{debtor.totalCases} case(s)</p>
                      <p className="font-semibold text-gray-900">{formatCurrency(debtor.totalDebt)}</p>
                    </div>
                    <Link
                      to={`/cases?debtor=${debtor.debtor_id}`}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      View Cases →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 text-sm text-gray-500">
            Showing {filteredDebtors.length} of {debtors.length} debtors
          </div>
        </div>
      </main>
    </div>
  );
}
